import { useState, useEffect } from "react";
import * as Sentry from "../../lib/errorReporting";
import { apiFetch, authedApiFetch } from "../../lib/api";
import { APP_VERSION, BUILD_TYPE } from "../../lib/constants";
import { getUserAgent, randomUUID } from "../../lib/platform";
import { track } from "../../lib/analytics";
import OfflineBanner from "../../components/ui/OfflineBanner";
import { JT } from "../../components/ui/jumbotronTokens";

function BugReporter({currentPage, onClose, user, isOnline, cloudSynced}) {
  const [featureArea, setFeatureArea] = useState("");
  const [description, setDescription] = useState("");
  const [stage, setStage] = useState("form"); // form | submitting | success | error
  const [idemKey, setIdemKey] = useState(null); // #915: reused across retries of one submission

  useEffect(() => {
    setStage("form");
    setDescription("");
    setFeatureArea("");
  }, []);

  const [offlineMsg, setOfflineMsg] = useState(false);
  useEffect(() => {
    if (!offlineMsg) return;
    const t = setTimeout(() => setOfflineMsg(false), 3000);
    return () => clearTimeout(t);
  }, [offlineMsg]);

  const FEATURE_AREAS = ["UI/UX","Infrastructure & Backend","AI & Machine Learning","Data & Analytics","Monetization","Growth / Community"];

  const getDevice = () => {
    const ua = getUserAgent();
    if (/iPhone/.test(ua)) return "iPhone";
    if (/iPad/.test(ua)) return "iPad";
    if (/Android/.test(ua)) return "Android";
    return "Desktop";
  };

  const submit = async () => {
    if (!isOnline) { setOfflineMsg(true); return; }
    if (!description.trim()) return;
    setStage("submitting");
    // #915: reuse the idempotency key across retries of THIS submission; cleared on
    // success so the next report mints a fresh one. A retry after a failure reuses it,
    // so the quota reserve replays instead of double-charging.
    const key = idemKey || randomUUID();
    setIdemKey(key);
    try {
      const fetchFn = user ? authedApiFetch : apiFetch;
      const res = await fetchFn("/api/report-bug", {
        method: "POST",
        headers: {"Content-Type": "application/json", "Idempotency-Key": key},
        body: JSON.stringify({
          featureArea: featureArea || undefined,
          description: description.trim(),
          appVersion: `v${APP_VERSION}`,
          buildType: BUILD_TYPE,
          page: currentPage,
          device: getDevice(),
          cloudSynced,
        }),
      });
      if (!res.ok) throw new Error("Failed to submit");
      try { await res.json(); } catch (_) {}
      setIdemKey(null); // success -> next report mints a fresh key
      setStage("success");
      track('bug_report_submitted');
    } catch (submitErr) {
      Sentry.captureException(submitErr, {
        tags: { function: 'submitBugReport', failureStage: 'bug-report-submit' },
        extra: {
          featureArea,
          appVersion: APP_VERSION,
        },
      });
      setStage("error");
    }
  };

  const inp = {width:"100%",background:JT.bg.well,border:`1px solid ${JT.border.mid}`,borderRadius:9,padding:"11px 13px",color:JT.text.body,fontSize:16,outline:"none",boxSizing:"border-box",fontFamily:"inherit"};
  const lbl = {...JT.type.label,color:JT.text.tertiary,marginBottom:5,display:"block",letterSpacing:"1px"};

  if (stage==="success") return (
    <div style={{textAlign:"center",padding:"40px 20px"}}>
      <div style={{fontSize:40,marginBottom:12}}>✅</div>
      <div style={{fontSize:15,fontWeight:700,color:JT.status.won.color,marginBottom:8}}>Bug Reported</div>
      <div style={{fontSize:12,color:JT.text.secondary,marginBottom:20}}>Your report has been logged in our Bug Tracker. Thanks for helping improve SlipTrack.</div>
      <button onClick={onClose} style={{background:JT.border.mid,border:"none",borderRadius:10,padding:"12px 24px",color:JT.text.body,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
        Done
      </button>
    </div>
  );

  if (stage==="error") return (
    <div style={{textAlign:"center",padding:"40px 20px"}}>
      <div style={{fontSize:40,marginBottom:12}}>⚠️</div>
      <div style={{fontSize:15,fontWeight:700,color:JT.status.lost.color,marginBottom:8}}>Submission Failed</div>
      <div style={{fontSize:12,color:JT.text.secondary,marginBottom:20}}>Something went wrong. Please try again.</div>
      <button onClick={()=>setStage("form")} style={{background:JT.border.mid,border:"none",borderRadius:10,padding:"12px 24px",color:JT.text.body,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
        Try Again
      </button>
    </div>
  );

  return (
    <div style={{padding:"8px 18px 48px"}}>
      <OfflineBanner visible={offlineMsg} onDismiss={()=>setOfflineMsg(false)}/>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
        <span style={{fontSize:17,fontWeight:800,color:JT.text.primary}}>Report a Bug</span>
        <button onClick={onClose} style={{background:"none",border:"none",color:JT.text.muted,fontSize:20,cursor:"pointer"}}>✕</button>
      </div>

      {/* Version badge */}
      <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center"}}>
        <div style={{fontSize:11,fontWeight:700,color:JT.text.primary,background:JT.bg.card,border:`1px solid ${JT.border.mid}`,borderRadius:6,padding:"4px 10px"}}>v{APP_VERSION}</div>
        <div style={{fontSize:11,fontWeight:700,color:"#f59e0b",background:"#1a1200",border:"1px solid #f59e0b33",borderRadius:6,padding:"4px 10px"}}>{BUILD_TYPE}</div>
        <div style={{fontSize:11,color:JT.text.tertiary}}>{getDevice()}</div>
      </div>

      <div style={{marginBottom:12}}>
        <label style={lbl}>What went wrong? *</label>
        <textarea style={{...inp,height:100,resize:"none"}} placeholder="What happened? What did you expect to happen?" value={description} onChange={e=>setDescription(e.target.value)}/>
      </div>

      <div style={{marginBottom:12}}>
        <label style={lbl}>Feature Area</label>
        <select style={inp} value={featureArea} onChange={e=>setFeatureArea(e.target.value)}>
          <option value="">Select (optional)</option>
          {FEATURE_AREAS.map(f=><option key={f}>{f}</option>)}
        </select>
      </div>
      <div style={{marginBottom:12,padding:"8px 12px",background:JT.bg.card,borderRadius:8,border:`1px solid ${JT.border.mid}`}}>
        <div style={{fontSize:11,color:JT.text.tertiary}}>💡 SEV level will be automatically assigned based on your description</div>
      </div>

      <div style={{background:JT.bg.well,borderRadius:8,padding:"10px 12px",marginBottom:16,border:`1px solid ${JT.border.default}`}}>
        <div style={{...JT.type.label,color:JT.text.tertiary,letterSpacing:"1px",marginBottom:4}}>AUTO-ATTACHED</div>
        <div style={{fontSize:11,color:JT.text.tertiary}}>v{APP_VERSION} · {BUILD_TYPE} · {currentPage} · {getDevice()}</div>
      </div>

      <button
        onClick={e => { e.stopPropagation(); submit(); }}
        disabled={!description.trim()||stage==="submitting"}
        style={{width:"100%",padding:"14px",background:description.trim()?"linear-gradient(135deg,#1d4ed8,#4f46e5)":JT.border.mid,border:"none",borderRadius:12,color:JT.text.primary,fontSize:14,fontWeight:800,cursor:description.trim()?"pointer":"default",fontFamily:"inherit"}}>
        {stage==="submitting"?"Submitting…":"Submit Bug Report →"}
      </button>
    </div>
  );
}

export default BugReporter;
